import React from 'react';
import PropTypes from 'prop-types';


const HistoryGraph = ({ data, title, color = '#3498db', dataKey = 'time', binSize = 0.3, onDeletePoint, referenceLines = [] }) => {
    // Filter out items that don't have this key or if it's 0
    const validData = data.filter(d => d[dataKey] != null && d[dataKey] > 0);

    if (!validData || validData.length === 0) {
        return (
            <div style={{ color: '#888', textAlign: 'center', padding: '40px' }}>
                No {dataKey} data for this mode yet.
            </div>
        );
    }

    const margin = { top: 20, right: 80, bottom: 40, left: 50, middle: 40 };
    const width = 900;
    const height = 300;
    const chartWidth = 550;
    const histWidth = width - margin.left - chartWidth - margin.middle - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Get values for the y-axis
    const values = validData.map(d => d[dataKey]);
    
    // Include reference lines in min/max calculation if they are relevant (time or firstShot)
    const refValues = referenceLines.map(l => l.value);
    const allYValues = [...values, ...refValues];
    
    const minValue = Math.min(...allYValues) * 0.9;
    const maxValue = Math.max(...allYValues) * 1.1;
    const valueRange = maxValue - minValue;

    // Helper to get X coordinate for line chart
    const getX = (index) => (index / (validData.length - 1 || 1)) * chartWidth;
    
    // Helper to get Y coordinate
    const getY = (val) => chartHeight - ((val - minValue) / valueRange) * chartHeight;

    const points = validData.map((d, i) => `${getX(i)},${getY(d[dataKey])}`).join(' ');
    
    const BELT_COLORS = {
        yellow: '#f1c40f',
        green: '#2ecc71',
        blue: '#3498db',
        purple: '#9b59b6',
        brown: '#8B4513',
        black: '#ecf0f1',
    };
    
    const getPointColor = (d) => {
        if (d.belt && BELT_COLORS[d.belt]) return BELT_COLORS[d.belt];
        return color;
    };

    // Calculate Histogram Bins (Vertical, using binSize prop)
    const histMinValue = Math.floor(Math.min(...values) / binSize) * binSize;
    const histMaxValue = Math.ceil(Math.max(...values) / binSize) * binSize;
    const numBins = Math.max(1, Math.round((histMaxValue - histMinValue) / binSize));
    const bins = Array(numBins).fill(0);
    
    validData.forEach(d => {
        let binIdx = Math.floor((d[dataKey] - histMinValue) / binSize);
        if (binIdx >= numBins) binIdx = numBins - 1;
        if (binIdx < 0) binIdx = 0;
        bins[binIdx]++;
    });

    const maxBinCount = Math.max(...bins, 1);
    const histStartX = margin.left + chartWidth + margin.middle;

    return (
        <div style={{ width: '100%', maxWidth: '950px', margin: '0 auto', marginBottom: '20px' }}>
            <h3 style={{ textAlign: 'center', color: '#ccc', fontSize: '14px', marginBottom: '10px' }}>{title}</h3>
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet">
                {/* ─── Line Chart Axes ─── */}
                <line x1={margin.left} y1={margin.top} x2={margin.left} y2={height - margin.bottom} stroke="#444" strokeWidth="1" />
                <line x1={margin.left} y1={height - margin.bottom} x2={margin.left + chartWidth} y2={height - margin.bottom} stroke="#444" strokeWidth="1" />

                {/* ─── Grid Lines (horizontal across both charts) ─── */}
                {[0, 0.25, 0.5, 0.75, 1].map(v => {
                    const y = margin.top + (chartHeight * (1 - v));
                    const label = (minValue + v * valueRange).toFixed(2);
                    return (
                        <React.Fragment key={v}>
                            <line x1={margin.left} y1={y} x2={width - margin.right} y2={y} stroke="#222" strokeWidth="1" strokeDasharray="4" />
                            <text x={margin.left - 10} y={y + 5} fill="#666" fontSize="10" textAnchor="end">{label}s</text>
                        </React.Fragment>
                    );
                })}

                {/* ─── Reference Lines (Belts) ─── */}
                {dataKey === 'time' && referenceLines.map((line, i) => {
                    const y = margin.top + getY(line.value);
                    if (y < margin.top || y > height - margin.bottom) return null;
                    return (
                        <React.Fragment key={i}>
                            <line 
                                x1={margin.left} 
                                y1={y} 
                                x2={margin.left + chartWidth} 
                                y2={y} 
                                stroke={line.color} 
                                strokeWidth="1" 
                                strokeDasharray="2,2" 
                                opacity="0.6"
                            />
                            <text 
                                x={margin.left + chartWidth + 5} 
                                y={y + 3} 
                                fill={line.color} 
                                fontSize="9" 
                                fontWeight="bold"
                                opacity="0.8"
                            >
                                {line.label} ({line.value.toFixed(2)}s)
                            </text>
                        </React.Fragment>
                    );
                })}

                {/* ─── Labels (X Axis - just start/end date) ─── */}
                <text x={margin.left} y={height - margin.bottom + 20} fill="#666" fontSize="10" textAnchor="start">Older</text>
                <text x={margin.left + chartWidth} y={height - margin.bottom + 20} fill="#666" fontSize="10" textAnchor="end">Newer</text>

                {/* ─── Line Path ─── */}
                <g transform={`translate(${margin.left}, ${margin.top})`}>
                    <polyline
                        points={points}
                        fill="none"
                        stroke={color}
                        strokeWidth="1.5"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                        opacity="0.3"
                    />
                    {/* Points */}
                    {validData.map((d, i) => (
                        <circle
                            key={i}
                            cx={getX(i)}
                            cy={getY(d[dataKey])}
                            r="4"
                            fill={getPointColor(d)}
                            stroke={getPointColor(d)}
                            strokeWidth="1"
                            onClick={() => onDeletePoint && onDeletePoint(d)}
                            style={{ cursor: onDeletePoint ? 'pointer' : 'default' }}
                        >
                            <title>
                                {`${new Date(d.timestamp).toLocaleString()}\n`}
                                {`Total: ${d.time?.toFixed(3)}s\n`}
                                {d.belt ? `Belt: ${d.belt.toUpperCase()}\n` : ''}
                                {d.firstShot ? `First: ${d.firstShot.toFixed(3)}s\n` : ''}
                                {d.avgSplit ? `Avg Split: ${d.avgSplit.toFixed(3)}s\n` : ''}
                                {d.totalClicks ? `Clicks: ${d.totalClicks}` : ''}
                            </title>
                        </circle>
                    ))}
                </g>

                {/* ─── Histogram ─── */}
                <g transform={`translate(${histStartX}, ${margin.top})`}>
                    {/* Histogram base line (X axis) */}
                    <line x1={0} y1={chartHeight} x2={histWidth} y2={chartHeight} stroke="#444" strokeWidth="1" />
                    <text x={histWidth / 2} y={chartHeight + 20} fill="#666" fontSize="10" textAnchor="middle">Distribution ({binSize}s)</text>

                    {bins.map((count, i) => {
                        if (count === 0) return null;
                        
                        const binW = histWidth / numBins;
                        const binH = (count / maxBinCount) * chartHeight;
                        const xLeft = i * binW;
                        const yTop = chartHeight - binH;
                        
                        return (
                            <g key={i}>
                                <rect
                                    x={xLeft + 1}
                                    y={yTop}
                                    width={Math.max(1, binW - 2)}
                                    height={binH}
                                    fill={color}
                                    opacity="0.6"
                                />
                                <text
                                    x={xLeft + binW / 2}
                                    y={yTop - 5}
                                    fill="#aaa"
                                    fontSize="9"
                                    textAnchor="middle"
                                >
                                    {count}
                                </text>
                                {/* X-axis label for bin start time */}
                                {(i % Math.ceil(numBins / 5) === 0 || i === numBins - 1) && (
                                    <text
                                        x={xLeft}
                                        y={chartHeight + 10}
                                        fill="#555"
                                        fontSize="8"
                                        textAnchor="middle"
                                    >
                                        {(histMinValue + i * binSize).toFixed(1)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </g>
            </svg>
        </div>
    );
};

HistoryGraph.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    title: PropTypes.string.isRequired,
    color: PropTypes.string,
    dataKey: PropTypes.string,
    binSize: PropTypes.number,
    onDeletePoint: PropTypes.func,
    referenceLines: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.number.isRequired,
        label: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired
    }))
};

export default HistoryGraph;
